import type { Locale } from '@/i18n'

interface ProblemPatternCopy {
  title: string
  line: string
}

const en: readonly ProblemPatternCopy[] = [
  {
    title: 'Documents people read and retype',
    line: 'Invoices, contracts, reports, emails: information that arrives as text and gets moved by hand into systems, summaries, or decisions.',
  },
  {
    title: 'Support queues answering the same questions',
    line: 'Customers and colleagues waiting for answers that already exist in your documentation, tickets, and product data.',
  },
  {
    title: 'Knowledge locked in heads and wikis',
    line: 'Onboarding questions, internal how-tos, and expert bottlenecks that an assistant grounded in your own material can absorb.',
  },
  {
    title: 'Decisions waiting on manual data pulls',
    line: 'Reports assembled by hand, numbers chased across tools, analyses that arrive too late to act on.',
  },
  {
    title: 'A product that needs AI to stay competitive',
    line: 'Your roadmap calls for an AI capability (agents, search, chat, automation) and it has to be production grade, not a demo.',
  },
]

const hu: readonly ProblemPatternCopy[] = [
  {
    title: 'Dokumentumok, amiket valaki elolvas és újra begépel',
    line: 'Számlák, szerződések, jelentések, e-mailek: megjön a szöveg, valaki elolvassa, aztán kézzel átviszi a rendszerbe vagy összefoglalót ír belőle.',
  },
  {
    title: 'Ügyfélszolgálat, ami ugyanazokra a kérdésekre válaszol',
    line: 'Az ügyfél és a kollégák olyan válaszra várnak, ami már ott van a dokumentációban, a korábbi ticketekben és a termékadatok között.',
  },
  {
    title: 'Tudás, ami fejekbe és wikikbe van zárva',
    line: 'Az új kollégának mindig ugyanazt kell elmagyarázni, a belső leírást senki nem találja, egy-két emberen pedig fennakad minden kérdés. Ezt egy asszisztens is át tudja venni, ha a saját anyagaidból tanul.',
  },
  {
    title: 'Döntések, amik egy kézi riportra várnak',
    line: 'Kézzel összerakott kimutatások, rendszerek közt összekeresett számok és elemzések, amik mindig későn érkeznek ahhoz, hogy lépni lehessen.',
  },
  {
    title: 'Egy termék, aminek AI kell, hogy versenyben maradjon',
    line: 'A terméktervben már ott van az AI (ágens, keresés, chat, automatizálás), és a napi működésben kell helytállnia, nem a bemutatón.',
  },
]

export const PROBLEM_PATTERNS: Record<Locale, readonly ProblemPatternCopy[]> = {
  en,
  hu,
}

export const PATTERNS_INTRO: Record<Locale, string> = {
  en: 'AI pays off where human time goes into repetitive work today, and that time could go into work that creates far more value. Five patterns that show up in almost every company:',
  hu: 'Az AI ott térül meg, ahol ma emberek végeznek ismétlődő munkát, pedig ugyanez az idő sokkal értékesebb feladatokra is mehetne. Öt tipikus eset, ami szinte minden cégnél előfordul:',
}

export const AUDIT_HOOK: Record<Locale, string> = {
  en: "Your pattern not on the list? Finding it is exactly what the audit is for. We bring no industry averages with us: the numbers come out of your own process.",
  hu: 'A te eseted nincs a listán? Annál jobb: pont az ilyen rejtett lehetőségek megtalálására való az AI-audit. Iparági átlagokat nem hozunk magunkkal: a számok a te folyamatodból jönnek ki.',
}

export const WHERE_AI_LABEL: Record<Locale, string> = {
  en: 'Where AI Pays Off',
  hu: 'Hol térül meg az AI',
}
